import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from 'components/ui/button';
import { ScrollArea } from 'components/ui/scroll-area';
import { checkSpanishLocale, extractLabelAndComment } from 'lib/utils';
import { QuestionnaireItem } from 'types';
import { useSearchParams } from 'react-router-dom';
import useRiskCheckerStore from 'store/risk-checker';
import { getQuestionInfo } from './data';
import { QUESTIONNAIRE_CODE } from 'lib/constants';

interface QuestionnaireContainerProps {
  children: React.ReactNode;
  questionData: QuestionnaireItem | undefined;
  onContinue: () => void;
  isLoading: boolean;
  disabled?: boolean;
  questionNumber: number;
}

export default function QuestionnaireContainer({
  children,
  questionData,
  onContinue,
  isLoading,
  disabled = false,
}: QuestionnaireContainerProps) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');

  const isSpanish = checkSpanishLocale();

  const engineName = useRiskCheckerStore((store) => store.engineName);

  const { label } = extractLabelAndComment(
    questionData?.[isSpanish ? 'spanish_question' : 'eng_question'] ?? ''
  );

  const questionInfo = getQuestionInfo(engineName || 'hypertension_risk');

  const questionNumber =
    (questionData?.code
      ? questionInfo.questionNumber[questionData.code as QUESTIONNAIRE_CODE]
      : 0) || 0;

  return (
    <div>
      <ScrollArea className='h-[calc(100vh-20.75rem)]'>
        <div className='flex-1 px-4 pt-4 pb-3'>
          <h2 className='text-lg font-semibold'>
            {t('question')} #{questionNumber}
          </h2>

          <div className='mt-3'>
            <p className='mb-4 text-base font-semibold'>{label}</p>
            <div>{children}</div>
          </div>
        </div>
      </ScrollArea>
      <div className='flex justify-center mt-3'>
        <Button
          className='px-6 h-9'
          onClick={onContinue}
          disabled={isLoading || disabled}
        >
          {isLoading && <Loader2 className='mr-2 animate-spin size-4' />}
          {code ? t('update') : t('continue')}
        </Button>
      </div>
    </div>
  );
}
